<template>
  <transition>
    <div class="mod-dial" @click="CATEGORIES_SET_CREATE_VISIBLE(false)">
      <div class="mod-cont" @click.stop>
        <span class="close-main" @click.stop="CATEGORIES_SET_CREATE_VISIBLE(false)">
          <i class="bi bi-x-lg"></i>
        </span>
        <div class="mod-body cat-manager">
          <h3 class="tab-title">
            {{ $t('CREATE_CATEGORY_TITLE') }}
            <span v-if="getProfile && getProfile.pageLimit" class="limit-info">
              ({{ getCategories.length }}/{{ getProfile.pageLimit }})
            </span>
          </h3>
          <!-- Create New Category -->
          <div class="category-pages">
            <form @submit.prevent="createCategory" class="inp-btn">
              <input
                type="text"
                class="inp-st"
                :placeholder="$t('CREATE_CATEGORY_PLACEHOLDER')"
                v-model="categoryName"
                maxlength="25"
              />
              <button 
                class="btn-st-red" 
                type="submit" 
                :disabled="isCategoryLimitReached"
              >
{{ $t('CREATE_CATEGORY_CREATE') }}
              </button>
            </form>
            
            <!-- Category limit info -->
            <div v-if="getProfile && getProfile.pageLimit" class="limit-info">
              <div v-if="isCategoryLimitReached" class="limit-warning">
                <p class="warning-text">
                  {{ $t('CREATE_CATEGORY_LIMIT_REACHED', { limit: getProfile.pageLimit }) }}
                </p>
              </div>
              <div v-else-if="remainingCategories <= 2" class="limit-approaching">
                <p class="info-text">
                  {{ $t('CREATE_CATEGORY_LIMIT_APPROACHING', { remaining: remainingCategories }) }}
                </p>
              </div>
            </div>
          </div>

          <!-- Selected Pages Preview -->
          <div class="category-pages" v-if="getSelectedPages.length">
            
            <p class="cat-chosen">{{ $t('CREATE_CATEGORY_SELECTED', { count: getSelectedPages.length }) }}:</p>
            <div class="sel-pages">
              <div class="sel-list">
                <div
                  class="sel-pg"
                  :key="index"
                  v-for="(page, index) in getSelectedPages"
                >
                  <img :src="page.owner_avatar" alt />
                  <div>
                    <p>{{ truncateName(page.owner_name) }}</p>
                    <span v-if="page.social == 'FB'">Facebook</span>
                    <span v-if="page.social == 'IG'">Instagram</span>
                    <span v-if="page.social == 'VK'">VK</span>
                  </div>
                  <span class="close" @click="removeFromSelection(page)"><i class="bi bi-x-lg"></i></span>
                </div>
              </div>
            </div>
          </div>
          <!-- Add to Existing Category -->
          <div class="category-pages" v-if="categoriesWithHover.length">
             <div class="mod-inner">
               <span class="text-header">{{ $t('CREATE_CATEGORY_EXISTING') }}</span>
               <ul class="res">
                 <li
                   :key="category.id"
                   v-for="category in categoriesWithHover"
                   @click.capture="addToExistingCategory(category)"
                 >
                   <span :class="`cat-indicator ${getCategoryColorClass(category.id)}`"></span>
                   <span class="caption">{{ category.name }} ({{ getCategoryPageCount(category) }} pages)</span>
                   <button type="button" class="btn-st-tr">
                     {{ $t('CREATE_CATEGORY_ADD') }}
                   </button>
                 </li>
               </ul>
             </div>
           </div>
           
          <!-- Container for inline alerts -->
          <div ref="alertContainer"></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import {
  CATEGORIES_CREATE,
  CATEGORIES_UPDATE,
  CATEGORIES_SET_CREATE_VISIBLE,
  CATEGORIES_ADD_PAGE,
} from "../../store/actions/categories";
import {
  INTRO_SET_SELECTED,
} from "../../store/actions/intro";

export default {
  name: "CreateCategory",
  computed: {
    ...mapGetters(["getSelectedPages", "getCategories", "getProfile"]),
    
    getCategoryPageCount() {
      return (category) => {
        if (category.pages) {
          return category.pages.length;
        } else if (category.page_ids) {
          return category.page_ids.length;
        }
        return 0;
      };
    },
    
    categoriesWithHover() {
      return this.getCategories.map(category => ({
        ...category
      }));
    },
    
    isCategoryLimitReached() {
      if (!this.getProfile || !this.getProfile.pageLimit) {
        return false;
      }
      return this.getCategories.length >= this.getProfile.pageLimit;
    },
    
    remainingCategories() {
      if (!this.getProfile || !this.getProfile.pageLimit) {
        return 0;
      }
      return Math.max(0, this.getProfile.pageLimit - this.getCategories.length);
    },
  },
  data() {
    return {
      categoryName: "",
    };
  },
  watch: {
    getSelectedPages: {
      handler(newPages) {
        // Close modal if all pages are removed
        if (newPages.length === 0) {
          this[CATEGORIES_SET_CREATE_VISIBLE](false);
        }
      },
      immediate: false,
    },
  },
  methods: {
    ...mapActions([CATEGORIES_CREATE, CATEGORIES_UPDATE, CATEGORIES_ADD_PAGE, INTRO_SET_SELECTED]),
    ...mapMutations([CATEGORIES_SET_CREATE_VISIBLE]),

    async createCategory() {
      if (!this.categoryName.trim()) {
        this.showInlineAlert(this.$t('CREATE_CATEGORY_NAME_REQUIRED'));
        return;
      }

      if (!this.getSelectedPages.length) {
        this.showInlineAlert(this.$t('CREATE_CATEGORY_PAGES_REQUIRED'));
        return;
      }

      // Check if category limit is reached
      if (this.isCategoryLimitReached) {
        this.showInlineAlert(this.$t('CREATE_CATEGORY_LIMIT_REACHED', { 
          limit: this.getProfile.pageLimit 
        }));
        return;
      }

      const pageIds = this.getSelectedPages.map(page => page.page_id);

      try {
        const categoryName = this.categoryName.trim();
        await this[CATEGORIES_CREATE]({
          name: categoryName,
          page_ids: pageIds
        });
        
        this.categoryName = "";
        this[CATEGORIES_SET_CREATE_VISIBLE](false);
        
        this.$store.dispatch('alerts/addAlert', {
          message: `Category "${categoryName}" created successfully`,
          color: 'success',
          duration: 5000
        });
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_ERROR'));
        }
      }
    },

    async addToExistingCategory(category) {
      if (!this.getSelectedPages.length) {
        this.showInlineAlert(this.$t('CREATE_CATEGORY_PAGES_REQUIRED'));
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const pagesToAdd = this.getSelectedPages;

      try {
        // Use individual page operations for better error handling and granular control
        const addPromises = pagesToAdd.map(async (page) => {
          try {
            await this[CATEGORIES_ADD_PAGE]({
              page_id: page.page_id,
              category_id: category.id
            });
            successCount++;
          } catch (error) {
            errorCount++;
            console.error(`Failed to add page ${page.owner_name} to category:`, error);
            // Handle specific error cases if needed
            if (error?.response?.data?.error_code) {
              // Could show individual page errors if needed
            }
          }
        });

        await Promise.all(addPromises);

        // Show appropriate success/error message
        if (successCount > 0 && errorCount === 0) {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_ADDED_SUCCESS', { categoryName: category.name }));
        } else if (successCount > 0 && errorCount > 0) {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_PARTIAL_SUCCESS', { 
            categoryName: category.name, 
            success: successCount, 
            total: pagesToAdd.length 
          }));
        } else {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_ADD_ERROR'));
        }

        // Close the modal after a short delay if all or most pages were added
        if (successCount > 0) {
          setTimeout(() => {
            this[CATEGORIES_SET_CREATE_VISIBLE](false);
          }, 1500);
        }
        
      } catch (error) {
        if (error?.response?.data?.error_code) {
          this.showInlineAlert(this.$t(error.response.data.error_code));
        } else {
          this.showInlineAlert(this.$t('CREATE_CATEGORY_ADD_ERROR'));
        }
      }
    },

    truncateName(name) {
      if (!name) return '';
      const maxLength = 25;
      return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
    },

    getCategoryColorClass(categoryId) {
      if (!categoryId) return 'gray-back';
      
      // Get color from localStorage synchronously 
      const stored = localStorage.getItem('categoryColors');
      const colors = stored ? JSON.parse(stored) : {};
      const color = colors[categoryId] || 'gray';
      
      return color + '-back';
    },

    removeFromSelection(page) {
      this[INTRO_SET_SELECTED](page);
    },

    showInlineAlert(message) {
      this.$alert.show({
        message: message,
        type: 'inline',
        color: '',
        component: this,
        target: this.$refs.alertContainer,
      });
    },
  },
};
</script>

<style>
</style> 